import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BREAKPOINT_L } from '../../common/constants/breakpoints';
import { dsClass } from '../../common/constants/dsClasses';
import { useTitle } from '../../common/hooks/useTitle.js';
import { isInBrowser } from '../../common/utils/ssrUtils';
import { isEppSolutionActive } from '../../common/utils/stateUtils';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { AccountManagerDetailsCard } from '../AccountManagerDetailsCard/AccountManagerDetailsCard.js';
import { ContactChannelsInfoCard } from '../ContactChannelsInfoCard/ContactChannelsInfoCard.js';
import { KukaSoittaaBanner } from '../KukaSoittaaOrder/KukaSoittaaBanner';
import { LinkListSummary, LinkListSummaryType } from '../LinkListSummary/LinkListSummary';
import { PageMargins } from '../PageMargins/PageMargins';
import { SelfServiceCampaign } from '../SelfServiceCampaign/SelfServiceCampaign';
import { SelfServiceHomeFavoritesAndOrderNew } from './SelfServiceHomeFavoritesAndOrderNew';
import { SelfServiceHomeHeroHeading } from './SelfServiceHomeHeroHeading';
import type {
  BillingAccountHeader,
  CustomerOrderHeader,
  InvoiceHeader,
  SubscriptionActionHeader,
  SubscriptionHeader,
  SupportCaseDataBundle,
} from '../../generated/api/models.js';

import './SelfServiceHome.scss';

export interface SelfServiceHomeProps {
  serviceCounts: Record<string, number>;
  serviceLoading: Record<string, boolean>;
  voiceSubscriptions?: SubscriptionHeader[];
  allInvoices?: InvoiceHeader[];
  billingAccounts?: BillingAccountHeader[];
  openInvoices?: InvoiceHeader[];
  customerOrders?: CustomerOrderHeader[];
  pendingRedeemRequest?: SubscriptionActionHeader[];
  supportCases?: SupportCaseDataBundle[];
  summaryLoading?: {
    invoices?: boolean;
    orders?: boolean;
    redeemRequests?: boolean;
    supportCases?: boolean;
  };
}

const useIsMobileView = (): boolean => {
  const [isMobileView, setIsMobileView] = useState(isInBrowser() && window.innerWidth < BREAKPOINT_L);

  useEffect(() => {
    if (!isInBrowser()) {
      return;
    }

    const handleResize = () => setIsMobileView(window.innerWidth < BREAKPOINT_L);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobileView;
};

const useIsFrosmoEmpty = (): [React.RefObject<HTMLDivElement | null>, boolean] => {
  const frosmoRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const checkContent = useCallback(() => {
    const element = frosmoRef.current;
    if (!element) {
      setIsEmpty(true);
      return;
    }

    const modFirst = element.querySelector('#frosmo-omaelisa-home-mod-2');
    const modSecond = element.querySelector('#frosmo-omaelisa-home-mod-2--2');
    const modFirstIsEmpty = !modFirst || modFirst.children.length === 0;
    const modSecondIsEmpty = !modSecond || modSecond.children.length === 0;

    setIsEmpty(modFirstIsEmpty && modSecondIsEmpty);
  }, []);

  useEffect(() => {
    checkContent();

    const element = frosmoRef.current;
    if (!element) {
      return;
    }

    const observer = new MutationObserver(checkContent);
    observer.observe(element, { childList: true });

    return () => observer.disconnect();
  }, [checkContent]);

  return [frosmoRef, isEmpty];
};

export const SelfServiceHome = ({
  serviceCounts,
  serviceLoading,
  voiceSubscriptions,
  allInvoices,
  billingAccounts,
  openInvoices,
  customerOrders,
  pendingRedeemRequest,
  supportCases,
  summaryLoading = {},
}: SelfServiceHomeProps) => {
  const { authenticatedUser } = useAuth();
  const account = useSessionData().companyInfo;

  const isEppActive = isEppSolutionActive(account);

  const isMobileView = useIsMobileView();
  const [frosmoRef, isFrosmoEmpty] = useIsFrosmoEmpty();

  useTitle();

  return (
    <div className="of-self-service-home">
      <PageMargins as="padding" className={`${dsClass.BACKGROUND_COLOR_NEUTRAL_200}`}>
        {authenticatedUser?.contact?.isPartOfCampaignGroup && <SelfServiceCampaign />}
        <SelfServiceHomeHeroHeading
          companyName={authenticatedUser?.companyName || ''}
          companyAccountsCount={(authenticatedUser?.secondaryAccounts?.length ?? 0) + 1}
          businessId={authenticatedUser?.businessId || ''}
          serviceCounts={serviceCounts}
          serviceLoading={serviceLoading}
        />
      </PageMargins>

      <div id="frosmo-omaelisa-home-mod-0"></div>
      <div id="frosmo-omaelisa-home-mod-1"></div>

      <PageMargins>
        <div className="of-self-service-home__content">
          <KukaSoittaaBanner className="of-self-service-home__content__kukasoittaa" />

          <div
            className={`of-self-service-home__content__favorites-and-order-new-wrapper${isFrosmoEmpty ? '' : ' frosmo-not-empty'}`}
          >
            <SelfServiceHomeFavoritesAndOrderNew
              voiceSubscriptions={voiceSubscriptions}
              invoices={allInvoices}
              billingAccounts={billingAccounts}
            />
            <div ref={frosmoRef} className="favorites-and-order__frosmo-wrapper">
              <div id="frosmo-omaelisa-home-mod-2"></div>
              <div id="frosmo-omaelisa-home-mod-2--2"></div>
            </div>
          </div>

          <CL.Grid className={`of-self-service-home__content__summary-section ${dsClass.MARGIN_BOTTOM_4}`}>
            <CL.GridRow className={dsClass.MARGIN_BOTTOM_4} gutter={!isMobileView}>
              <CL.GridCol
                className={classNames(dsClass.PADDING_LEFT_0, isMobileView && dsClass.MARGIN_BOTTOM_4)}
                colWidth={6}
              >
                <LinkListSummary
                  linkListSummaryType={LinkListSummaryType.INVOICES}
                  data={openInvoices}
                  loading={summaryLoading.invoices}
                />
              </CL.GridCol>
              <CL.GridCol className={dsClass.PADDING_RIGHT_0} colWidth={6}>
                {isEppActive ? (
                  <LinkListSummary
                    linkListSummaryType={LinkListSummaryType.REDEEM_REQUESTS}
                    data={pendingRedeemRequest}
                    authenticatedUser={authenticatedUser}
                    loading={summaryLoading.redeemRequests}
                  />
                ) : (
                  <LinkListSummary
                    linkListSummaryType={LinkListSummaryType.ORDERS}
                    data={customerOrders}
                    authenticatedUser={authenticatedUser}
                    loading={summaryLoading.orders}
                  />
                )}
              </CL.GridCol>
            </CL.GridRow>
            <CL.GridRow gutter={isEppActive && !isMobileView}>
              <CL.GridCol
                className={classNames(dsClass.PADDING_LEFT_0, isMobileView && dsClass.MARGIN_BOTTOM_4)}
                colWidth={isEppActive ? 6 : 0}
              >
                {isEppActive && (
                  <LinkListSummary
                    linkListSummaryType={LinkListSummaryType.ORDERS}
                    data={customerOrders}
                    authenticatedUser={authenticatedUser}
                    loading={summaryLoading.orders}
                  />
                )}
              </CL.GridCol>
              <CL.GridCol className={dsClass.PADDING_RIGHT_0} colWidth={isEppActive ? 6 : 12}>
                <LinkListSummary
                  linkListSummaryType={LinkListSummaryType.SUPPORT_CASES}
                  data={supportCases}
                  authenticatedUser={authenticatedUser}
                  loading={summaryLoading.supportCases}
                />
              </CL.GridCol>
            </CL.GridRow>
          </CL.Grid>

          <CL.Grid className="of-self-service-home__content__contact-info">
            <CL.GridRow className={dsClass.MARGIN_BOTTOM_4} gutter={!isMobileView}>
              <CL.GridCol
                className={classNames(dsClass.PADDING_LEFT_0, isMobileView && dsClass.MARGIN_BOTTOM_4)}
                colWidth={6}
              >
                {account?.accountManager && <AccountManagerDetailsCard accountManager={account.accountManager} />}
              </CL.GridCol>
              <CL.GridCol className={dsClass.PADDING_RIGHT_0} colWidth={6}>
                <ContactChannelsInfoCard />
              </CL.GridCol>
            </CL.GridRow>
          </CL.Grid>

          <div id="frosmo-omaelisa-home-mod-3"></div>
        </div>
      </PageMargins>
    </div>
  );
};
